/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Box, Button, Typography, Stack, Card, CardContent, CardActionArea, Tooltip, CircularProgress } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Path, TechwiseOffice } from "../../utils/Interfaces";
import CreateStorageLocationModal from "./CreateTechwiseOfficeModal";
import { selectExistingTechwiseOffices, selectExistingTechwiseOfficesLoaded } from "./techwiseOfficesSlice";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { DomainAdd } from "@mui/icons-material";
import { updateTechwiseOffices } from "./techwiseOfficesFunctions";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const TechwiseOfficesList = () => {
    const [officeCreateModalOpen, setOfficeCreateModalOpen] = useState<boolean>(false);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const navigate: NavigateFunction = useNavigate();

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);
    const techwiseOfficesLoaded: boolean = useAppSelector(selectExistingTechwiseOfficesLoaded);

    let breadcrumbPaths: Path[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Parts/Materials", route: "/?section=materials" },
        { text: "Techwise Offices", route: "/materials/techwise-offices" },
    ];

    if (!techwiseOfficesLoaded) {
        return (
            <Box>
                <StyledBreadcrumbs paths={breadcrumbPaths} />
                <Stack direction="row" justifyContent="center" sx={{ mt: 40 }}>
                    <CircularProgress size={60} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <Tooltip title="Add a new location">
                <Button variant="contained" onClick={() => setOfficeCreateModalOpen(true)} sx={{ mt: 1 }}>
                    <DomainAdd fontSize="small" />
                    &nbsp; Add New Office
                </Button>
            </Tooltip>

            <CreateStorageLocationModal
                open={officeCreateModalOpen}
                handleClose={() => setOfficeCreateModalOpen(false)}
                onTechwiseOfficeCreated={() => updateTechwiseOffices(dispatch, () => {})}
            />

            {techwiseOffices.length === 0 && (
                <Typography align="center" sx={{ mt: 10 }}>
                    No offices, please create an office to continue.
                </Typography>
            )}

            {techwiseOffices.map((office: TechwiseOffice, index: number) => {
                return (
                    <Card key={index} sx={{ mt: 3 }}>
                        <CardActionArea onClick={() => navigate(`/materials/techwise-office/${office.id}`)}>
                            <CardContent>
                                <Typography sx={{ fontWeight: "bold" }}>{office.name}</Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <LocationOnOutlinedIcon fontSize="large" />
                                    <Box>
                                        <Typography>{office.street_address}</Typography>
                                        <Typography>
                                            {office.city}, {office.state} {office.zip_code}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                );
            })}
        </Box>
    );
};

export default TechwiseOfficesList;
